import { useEffect } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

/**
 * Tant qu'un fil de discussion est OUVERT et VISIBLE, on renouvelle la date de
 * lecture (`mark_discussion_read`) à intervalle régulier. Le serveur s'en sert
 * pour NE PAS notifier quelqu'un qui lit déjà le fil
 * (`supabase/functions/_shared/discussion-notify.ts` — la fenêtre serveur
 * `DISCUSSION_VIEWING_WINDOW_MS` doit rester > 2 × cet intervalle).
 *
 * Appel RPC direct, SANS invalidation React Query (le marquage « lu » à
 * l'ouverture s'en charge déjà) : un battement toutes les 20 s ne doit pas
 * relancer de requêtes de liste.
 */
export const DISCUSSION_READ_HEARTBEAT_MS = 20_000;

const db = supabase as unknown as SupabaseClient;

function beat(discussionId: string) {
  void db.rpc('mark_discussion_read', { p_discussion_id: discussionId }).then(
    () => undefined,
    () => undefined, // best-effort
  );
}

export function useDiscussionReadHeartbeat(discussionId: string | undefined): void {
  useEffect(() => {
    if (!discussionId) return undefined;
    const isVisible = () => document.visibilityState === 'visible';
    const timer = setInterval(() => {
      if (isVisible()) beat(discussionId);
    }, DISCUSSION_READ_HEARTBEAT_MS);
    // Retour sur l'onglet/l'app : on est de nouveau « en train de lire ».
    const onVisibility = () => {
      if (isVisible()) beat(discussionId);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [discussionId]);
}
